import ModalComponent from 'react-modal';
import classNames from 'classnames';
import Icon from '../../atom/Icon';
import useIsClientSide from '@mediashop/app/hooks/useIsClientSide';
import { ModalProps } from './index';

// Set App element for accessibility
try {
    ModalComponent.setAppElement('#app');
} catch {
    // Ignore SSR Error
}

/**
 * Modal component. Can be used to open a modal with an acompanying overlay.
 * Add content as children.
 *
 * Make sure to handle onRequestClose (triggered on ESC-key & click on overlay)
 */
export default function Modal({
    footer,
    isOpen,
    onRequestClose,
    showCloseButton = true,
    background = 'white-true',
    children,
    bodyOpenClassName,
    overlayClassName,
    widthClassName,
    modalBodyClassName,
}: ModalProps): JSX.Element {
    const isClientSide = useIsClientSide();

    if (!onRequestClose && showCloseButton) {
        console.warn('If you want to show a close-button, you need to handle onRequestClose.');
    }

    const onAfterOpenHandler = () => {
        const scrollY = window.scrollY;
        document.querySelector('body')?.classList.add('scroll-lock--modal');
        document.body.style.top = `-${scrollY}px`;
    };

    const onAfterCloseHandler = () => {
        const scrollY = parseInt(document.body.style.top, 10);
        document.querySelector('body')?.classList.remove('scroll-lock--modal');
        document.body.removeAttribute('style');
        window.scrollTo(0, Math.abs(scrollY));
    };

    // SSR has problems with finding the correct element
    const useAriaHide = isClientSide;

    return (
        <ModalComponent
            isOpen={isOpen}
            onAfterOpen={onAfterOpenHandler}
            onAfterClose={onAfterCloseHandler}
            onRequestClose={onRequestClose}
            className={classNames('modal', `modal--bg-${background}`, widthClassName)}
            bodyOpenClassName={classNames('page--overlay-open', bodyOpenClassName)}
            overlayClassName={classNames('page__overlay', overlayClassName)}
            ariaHideApp={useAriaHide}
        >
            <div className="modal__inner">
                {showCloseButton ? (
                    <button aria-label="modal-close" className="modal__close" onClick={onRequestClose}>
                        <Icon name="Close" />
                    </button>
                ) : null}
                <div className={classNames('modal__body', modalBodyClassName)}>{children}</div>

                {footer ? <div className="modal__footer">{footer}</div> : null}
            </div>
        </ModalComponent>
    );
}
